import { lazy, type ComponentType } from 'react'

export const RELOAD_FLAG = 'route-chunk-reloaded'

// sessionStorage может быть недоступен (приватный режим, заблокированные куки) —
// без него теряем только защиту от повторной перезагрузки.
function readFlag(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_FLAG) === '1'
  } catch {
    return false
  }
}

function writeFlag(value: boolean): void {
  try {
    if (value) sessionStorage.setItem(RELOAD_FLAG, '1')
    else sessionStorage.removeItem(RELOAD_FLAG)
  } catch {
    // хранилище недоступно — молча продолжаем
  }
}

/**
 * Чанк маршрута может не догрузиться: после выката старые ассеты удаляются, и
 * вкладка, открытая до деплоя, просит файл, которого уже нет. В Sentry это
 * приходило как «Cannot read properties of undefined» (WEB-8) — модуль
 * резолвился пустым. Пробуем ещё раз, затем один раз перезагружаем страницу,
 * чтобы получить свежий index.html с актуальными хешами.
 */
export async function loadRouteModule<T>(
  load: () => Promise<Record<string, unknown> | undefined>,
  exportName: string,
  reload: () => void = () => window.location.reload(),
): Promise<{ default: T }> {
  for (let attempt = 0; attempt < 2; attempt++) {
    const module = await load().catch(() => undefined)
    const component = module?.[exportName]

    if (component) {
      writeFlag(false)

      return { default: component as T }
    }
  }

  if (readFlag()) {
    throw new Error(`Не удалось загрузить модуль маршрута: ${exportName}`)
  }

  writeFlag(true)
  reload()

  // Страница уже перезагружается — этот промис не должен разрешиться.
  return new Promise<never>(() => {})
}

// Маршруты рендерятся без пропсов, поэтому конкретный тип пропсов здесь не нужен.
type RouteComponent = ComponentType<Record<string, unknown>>

export function lazyRoute(
  load: () => Promise<Record<string, unknown> | undefined>,
  exportName: string,
) {
  return lazy(() => loadRouteModule<RouteComponent>(load, exportName))
}
